import React, { useEffect, useMemo, useRef, useState } from "react"
import SkyLight from "react-skylight"
import { TargetDistribution } from "../../types/MarketingCampaign"
import Switch from "../../Components/Switch/Switch"
import ButtonGroup from "../../Components/ButtonGroup/ButtonGroup"
import { computeEquitableDistribution } from "../../Lib/MarketingCampaign"

enum AssignmentInputMode
{
    NUMBER = "number",
    PERCENT = "percent",
}

type Props = {
    isOpen: boolean
    sellers: TargetDistribution[]
    selectedSellerIds: number[]
    // Répartition déjà validée précédemment (nombre de contacts par commercial) : sert à
    // pré-remplir la modale si on la rouvre après une première validation.
    previousAssignments: Record<number, number>
    // Solde total des contacts non assignés à répartir : sert de base au calcul du mode pourcentage
    // et à la détection d'un dépassement de la saisie manuelle.
    totalAvailable: number
    // Libellé métier du type de vendeur ciblé par la campagne ("Vendeur" ou "Conseiller"
    // pour une campagne ADV), utilisé pour les textes de la modale.
    sellerLabel: string
    sellerLabelPlural: string
    onClose: () => void
    // manualAssignments : nombre exact de contacts (déjà converti depuis le pourcentage le cas
    // échéant) voulu pour chaque commercial ayant une saisie renseignée. Les commerciaux cochés
    // sans entrée dans cette map reçoivent leur part via la répartition équitable existante.
    onValidate: (sellerIds: number[], manualAssignments: Record<number, number>) => void
}

/**
 * Choix des commerciaux sur lesquels répartir le solde des contacts non assignés.
 * La sélection est locale à la modale : elle n'est remontée qu'à la validation.
 */
export default function ModalRepartitionSolde(props: Props) {

    const modalRef = useRef<any>(null)
    const [ draftIds, setDraftIds ] = useState<number[]>([])
    const [ inputMode, setInputMode ] = useState<AssignmentInputMode>(AssignmentInputMode.NUMBER)
    // Répartition équitable ON : les champs se remplissent automatiquement (et ne sont plus
    // éditables) au fur et à mesure qu'on coche des commerciaux. OFF : saisie manuelle libre.
    const [ isEquitable, setIsEquitable ] = useState(true)
    // Saisie manuelle brute (string) par commercial, utilisée uniquement quand isEquitable est faux.
    const [ manualDrafts, setManualDrafts ] = useState<Record<number, string>>({})

    // SkyLight ne s'ouvre qu'en impératif : on synchronise avec la prop isOpen, et on réinitialise
    // le brouillon sur la sélection déjà validée à chaque ouverture.
    useEffect(() => {
        if (!modalRef.current) {
            return
        }
        if (props.isOpen) {
            setDraftIds(props.selectedSellerIds)
            setInputMode(AssignmentInputMode.NUMBER)
            // Une répartition déjà validée est ré-affichée telle quelle, en saisie manuelle
            // (elle peut avoir été équitable ou non, seules les valeurs exactes sont connues) ;
            // sans répartition préalable, on repart en mode équitable par défaut.
            const previousEntries = Object.entries(props.previousAssignments)
            if (previousEntries.length > 0) {
                const drafts: Record<number, string> = {}
                previousEntries.forEach(([ sellerId, count ]) => {
                    drafts[Number(sellerId)] = String(count)
                })
                setManualDrafts(drafts)
                setIsEquitable(false)
            } else {
                setManualDrafts({})
                setIsEquitable(true)
            }
            modalRef.current.show()
        } else {
            modalRef.current.hide()
        }
    }, [ props.isOpen ])

    const allSelected = props.sellers.length > 0
        && props.sellers.every((seller: TargetDistribution) => draftIds.indexOf(seller.followed_by.id) !== -1)

    const toggleAll = (checked: boolean) => {
        setDraftIds(checked ? props.sellers.map((seller: TargetDistribution) => seller.followed_by.id) : [])
    }

    const toggleSeller = (sellerId: number, checked: boolean) => {
        setDraftIds(checked
            ? [ ...draftIds, sellerId ]
            : draftIds.filter((id: number) => id !== sellerId))
        // Décocher un commercial rend sa saisie manuelle sans objet : on l'efface pour éviter
        // qu'elle ne réapparaisse, incohérente, si on le recoche plus tard.
        if (!checked) {
            setManualDrafts((current: Record<number, string>) => {
                const { [sellerId]: _removed, ...rest } = current
                return rest
            })
        }
    }

    // N'autorise que les chiffres et un unique séparateur décimal (virgule ou point) : les inputs
    // sont en type="text" pour pouvoir accepter la virgule, il faut donc filtrer nous-mêmes les
    // caractères invalides qu'un input number aurait bloqués (ou pas, selon le navigateur).
    const sanitizeNumericInput = (rawValue: string): string => {
        const digitsAndSeparators = rawValue.replace(/[^0-9.,]/g, "")
        const firstSeparatorIndex = digitsAndSeparators.search(/[.,]/)
        if (firstSeparatorIndex === -1) {
            return digitsAndSeparators
        }
        return digitsAndSeparators.slice(0, firstSeparatorIndex + 1)
            + digitsAndSeparators.slice(firstSeparatorIndex + 1).replace(/[.,]/g, "")
    }

    const setManualDraft = (sellerId: number, rawValue: string) => {
        const sanitizedValue = sanitizeNumericInput(rawValue)
        setManualDrafts((current: Record<number, string>) => ({ ...current, [sellerId]: sanitizedValue }))
    }

    // Convertit une saisie manuelle brute en nombre absolu de contacts, selon le mode donné.
    const toAbsoluteCount = (rawValue: string, mode: AssignmentInputMode, totalAvailable: number): number | null => {
        if (rawValue === "") {
            return null
        }
        const numericValue = Number(rawValue.replace(",", "."))
        if (Number.isNaN(numericValue)) {
            return null
        }
        return mode === AssignmentInputMode.PERCENT
            ? Math.round((numericValue / 100) * totalAvailable)
            : Math.round(numericValue)
    }

    // Change le mode de saisie en convertissant les valeurs déjà tapées, sinon "5" en nombre
    // devient "5" en pourcentage sans changer de sens (5 contacts vs 5 % du solde).
    const changeInputMode = (nextMode: AssignmentInputMode) => {
        if (nextMode === inputMode) {
            return
        }
        setManualDrafts((current: Record<number, string>) => {
            const converted: Record<number, string> = {}
            Object.entries(current).forEach(([ sellerId, rawValue ]) => {
                const absoluteCount = toAbsoluteCount(rawValue, inputMode, props.totalAvailable)
                if (absoluteCount === null) {
                    converted[Number(sellerId)] = rawValue
                    return
                }
                converted[Number(sellerId)] = nextMode === AssignmentInputMode.PERCENT
                    ? (props.totalAvailable > 0 ? String(Math.round((absoluteCount / props.totalAvailable) * 10000) / 100) : "0")
                    : String(absoluteCount)
            })
            return converted
        })
        setInputMode(nextMode)
    }

    const checkedSellers: TargetDistribution[] = useMemo(() => (
        props.sellers.filter((seller: TargetDistribution) => draftIds.indexOf(seller.followed_by.id) !== -1)
    ), [ props.sellers, draftIds ])

    // Répartition équitable calculée sur les commerciaux cochés : sert à la fois à préremplir
    // l'aperçu affiché et, en mode équitable, à l'affectation réellement envoyée à la validation —
    // garantissant que ce qui est affiché est exactement ce qui sera attribué.
    const equitableTakesBySellerId: Record<number, number> = useMemo(() => {
        const takes = computeEquitableDistribution(checkedSellers, props.totalAvailable)
        const bySellerId: Record<number, number> = {}
        checkedSellers.forEach((seller: TargetDistribution, index: number) => {
            bySellerId[seller.followed_by.id] = takes[index]
        })
        return bySellerId
    }, [ checkedSellers, props.totalAvailable ])

    const manualAssignments: Record<number, number> = useMemo(() => {
        if (isEquitable) {
            return equitableTakesBySellerId
        }
        const assignments: Record<number, number> = {}
        draftIds.forEach((sellerId: number) => {
            const rawValue = manualDrafts[sellerId]
            if (rawValue === undefined) {
                return
            }
            const absoluteCount = toAbsoluteCount(rawValue, inputMode, props.totalAvailable)
            if (absoluteCount !== null) {
                assignments[sellerId] = absoluteCount
            }
        })
        return assignments
    }, [ isEquitable, equitableTakesBySellerId, draftIds, manualDrafts, inputMode, props.totalAvailable ])

    // Valeur affichée dans le champ d'un commercial : la répartition équitable calculée en mode
    // automatique, ou la saisie manuelle brute en mode libre.
    const displayedValue = (sellerId: number): string => {
        if (isEquitable) {
            if (equitableTakesBySellerId[sellerId] === undefined) {
                return ""
            }
            const absoluteCount = equitableTakesBySellerId[sellerId]
            return inputMode === AssignmentInputMode.PERCENT
                ? (props.totalAvailable > 0 ? String(Math.round((absoluteCount / props.totalAvailable) * 10000) / 100) : "0")
                : String(absoluteCount)
        }
        return manualDrafts[sellerId] || ""
    }

    const totalManuallyAssigned = Object.values(manualAssignments).reduce((sum: number, value: number) => sum + value, 0)
    const isOverAssigned = totalManuallyAssigned > props.totalAvailable
    const assignedPercentage = props.totalAvailable > 0 ? Math.round((totalManuallyAssigned / props.totalAvailable) * 100) : 0

    return (
        <SkyLight
            hideOnOverlayClicked
            ref={modalRef}
            afterClose={props.onClose}
            title="Répartir le solde restant"
            titleStyle={{ padding: 10, margin: 0, color: "white" }}
            closeButtonStyle={{ color: "white" }}
            dialogStyles={{
                padding: 0, width: "60%", maxWidth: "800px", minWidth: "300px", height: "auto",
                top: "10%", left: "0", right: "0", margin: "auto", zIndex: 9999, background: "#efefef",
            }}
        >
            <div style={{ padding: "15px" }}>
                <p className="alert alert-info">
                    {isEquitable
                        ? `Les contacts non assignés seront répartis équitablement entre les ${props.sellerLabelPlural.toLowerCase()} sélectionnés.`
                        : `Saisissez, pour chaque ${props.sellerLabel.toLowerCase()} sélectionné, le nombre exact (ou le pourcentage) de contacts non assignés à lui attribuer.`}
                </p>

                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "10px", flexWrap: "wrap", gap: "10px" }}>
                    <div>
                        <input
                            id="repartition-select-all"
                            type="checkbox"
                            checked={allSelected}
                            onChange={(e) => toggleAll(e.target.checked)}
                        />
                        <label htmlFor="repartition-select-all" style={{ marginLeft: "10px" }}>Sélectionner tous</label>
                    </div>
                    <div style={{ display: "flex", alignItems: "center", gap: "20px", flexWrap: "wrap" }}>
                        <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                            <p style={{ margin: 0 }}>Répartir équitablement</p>
                            <Switch checked={isEquitable} setChecked={setIsEquitable}/>
                        </div>
                        <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                            <p style={{ margin: 0 }}>Saisie en</p>
                            <ButtonGroup
                                color="#419BF9"
                                style={{ fontSize: "0.8em" }}
                                options={[
                                    { value: AssignmentInputMode.NUMBER, label: "Nombre" },
                                    { value: AssignmentInputMode.PERCENT, label: "Pourcentage" },
                                ]}
                                value={inputMode}
                                onChange={(mode) => changeInputMode(mode as AssignmentInputMode)}
                            />
                        </div>
                    </div>
                </div>

                <div style={{ maxHeight: "40vh", overflowY: "auto" }}>
                    <table className="table table-striped modal-repartition-table">
                        <thead>
                        <tr>
                            <th/>
                            <th>{props.sellerLabel}</th>
                            <th>Relations affectées</th>
                            <th>Affectation</th>
                            <th>Solde des relations</th>
                            <th>Affectation solde restant</th>
                        </tr>
                        </thead>
                        <tbody>
                        {props.sellers.length !== 0 ? props.sellers.map((seller: TargetDistribution) => {
                            const isSelected = draftIds.indexOf(seller.followed_by.id) !== -1

                            return (
                                <tr key={seller.followed_by.id}>
                                    <td>
                                        <input
                                            id={`repartition-seller-${seller.followed_by.id}`}
                                            type="checkbox"
                                            checked={isSelected}
                                            onChange={(e) => toggleSeller(seller.followed_by.id, e.target.checked)}
                                        />
                                    </td>
                                    <td>
                                        <label htmlFor={`repartition-seller-${seller.followed_by.id}`} style={{ fontWeight: "normal", marginBottom: 0 }}>
                                            {seller.followed_by.name}
                                        </label>
                                    </td>
                                    <td>{seller.followed_with_assignment_count + (seller.selected_targets || 0)} / {seller.targets_count}</td>
                                    <td>{seller.selected_targets || 0}</td>
                                    <td>{seller.solde || 0}</td>
                                    <td>
                                        <div style={{ display: "flex", alignItems: "center", gap: "5px" }}>
                                            <div>
                                                <input
                                                    type="text"
                                                    inputMode="decimal"
                                                    style={{ width: "70px" }}
                                                    disabled={!isSelected || isEquitable}
                                                    value={displayedValue(seller.followed_by.id)}
                                                    placeholder="0"
                                                    onChange={(e) => setManualDraft(seller.followed_by.id, e.target.value)}
                                                />
                                                <span>{inputMode === AssignmentInputMode.PERCENT ? "%" : ""}</span>
                                            </div>
                                            {inputMode === AssignmentInputMode.PERCENT && manualAssignments[seller.followed_by.id]
                                                ? <span style={{ color: "#666", whiteSpace: "nowrap", fontSize: "0.8em" }}>
                                                    ({manualAssignments[seller.followed_by.id]} contact{manualAssignments[seller.followed_by.id] > 1 ? "s" : ""})
                                                </span>
                                                : null}
                                        </div>
                                    </td>
                                </tr>
                            )
                        }) : (
                            <tr>
                                <td colSpan={6}>Aucun {props.sellerLabel.toLowerCase()} disponible pour cette occurrence.</td>
                            </tr>
                        )}
                        </tbody>
                    </table>
                </div>

                {isOverAssigned ? (
                    <p style={{ color: "#e08e0b", marginTop: "10px" }}>
                        ⚠ L'affectation manuelle ({totalManuallyAssigned} contacts) dépasse le solde disponible ({props.totalAvailable} contacts).
                    </p>
                ) : (
                    <p style={{ marginTop: "10px", color: totalManuallyAssigned === props.totalAvailable ? "green" : undefined }}>
                        {totalManuallyAssigned} / {props.totalAvailable} contacts affectés ({assignedPercentage}%)
                        {totalManuallyAssigned < props.totalAvailable ? ` (${props.totalAvailable - totalManuallyAssigned} restants)` : ""}
                    </p>
                )}

                <div style={{ display: "flex", justifyContent: "flex-end", gap: "10px", marginTop: "15px" }}>
                    <button className="btn btn-default" onClick={props.onClose}>Annuler</button>
                    <button
                        className="btn btn-default"
                        disabled={draftIds.length === 0 || isOverAssigned}
                        onClick={() => props.onValidate(draftIds, manualAssignments)}
                    >
                        Répartir
                    </button>
                </div>
            </div>
        </SkyLight>
    )
}
